import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Form, Modal, Spinner } from 'react-bootstrap';
import {
  useLazyGetCalendarsQuery,
  useLazyGetCalendarEventsQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useDeleteCalendarEventMutation,
  useInviteCalendarAttendeesMutation,
} from '../app/api';
import { useIsMobile } from './utils';
import { graphCalendarRequest } from '../msalConfig';
import useGraphToken from './useGraphToken';
import {
  HOUR_H, GUTTER_W, MAX_OVERLAY_CALENDARS,
  layoutDay, hourRange, eventBox, hourLabel, attendeeSplit,
  attendeeDiff, onlyAddsAttendees, parseAttendees, resolveCalendarColors,
  buildCategoryColors, colorForEvent,
} from './calendar/dayGridModel';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const PATH = '/calendar';
const WRAP = { minWidth: 0, overflowWrap: 'anywhere' };
const TAP = 44;
// One all-day chip. The band reserves a whole number of these across every
// column so the hour grid below stays level day to day.
const ALL_DAY_H = 18;

// Meetings still owned by a pre-merger account. The marker colour must NOT be one
// of the four CALENDAR_COLORS in dayGridModel — magenta was the obvious "bright"
// pick and is exactly what a magenta-assigned calendar already uses, which would
// have made the warning read as just another calendar.

/* The day-column header and all-day band, as style factories.
 *
 * The hour gutter renders invisible copies of both so its labels line up with
 * the grid. It used to carry a hardcoded `paddingTop: 26` instead, which was
 * only ever right when the week had no all-day items — with three birthdays in
 * view every timed event drew ~58px low, which reads as "the calendar is an
 * hour off". Deriving both sides from these means they can't drift again. */
const colHeadStyle = (isToday) => ({
  textAlign: 'center', padding: '0.3rem 0', fontSize: '0.75rem', fontWeight: 700,
  color: isToday ? brand.tealBlue : brand.charcoal,
  background: isToday ? 'rgba(0,105,166,0.06)' : 'transparent',
});
const allDayBandStyle = (rows) => ({
  height: rows * ALL_DAY_H + 4, padding: '0 2px 2px',
  borderBottom: `2px solid ${brand.softGray}`, overflow: 'hidden',
});
const ALL_DAY_MAX_ROWS = 3;
// Single-line clip. Distinct from WRAP (which breaks anywhere) — that's right for
// long unbroken email subjects in a full-width list, wrong for a narrow column.
const CLIP = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/* Calendar — shared Outlook calendars, with editing.
 *
 * The only page that WRITES to Microsoft. Every save or delete emails real
 * attendees, so each one is confirmed with its blast radius first. The layout
 * maths lives in calendar/dayGridModel.js (pure, tested separately); this file
 * is rendering, state, and the write flows.
 */

// ── date helpers (browser-local, used only for picking the window) ───────────
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => addDays(d, -d.getDay());
const prettyDay = (isoDate) => {
  const [y, m, dd] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

/** Local datetime-input value ⇄ the UTC string the API wants.
 *  The API owns display formatting; these two only exist for the <input>. */
const toInputValue = (utcIso) => {
  if (!utcIso) return '';
  const d = new Date(utcIso.endsWith('Z') ? utcIso : `${utcIso}Z`);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromInputValue = (v) => (v ? new Date(v).toISOString().replace(/\.\d{3}Z$/, '') : '');

// ── one day column ──────────────────────────────────────────────────────────

function DayColumn({
  day, events, calendarColors, categoryColors, originMin, gridH,
  nowMinutes, isToday, allDayRows, onPick,
}) {
  const { allDay, placed, untimed } = layoutDay(events);
  const shown = allDay.slice(0, allDayRows);
  const hidden = allDay.length - shown.length;

  return (
    <div style={{ flex: 1, minWidth: 0, borderLeft: `1px solid ${brand.softGray}` }}>
      <div style={colHeadStyle(isToday)}>{prettyDay(day)}</div>

      {/* All-day band. Every column reserves the SAME height — `allDayRows` is
          computed once across the whole week — because otherwise a Sunday with
          two birthdays pushes its grid down and 9 AM stops being level with
          Tuesday's 9 AM. Outlook pins this band above the grid for the same
          reason. Overflow collapses to "+N" rather than growing without bound. */}
      {allDayRows > 0 && (
        <div style={allDayBandStyle(allDayRows)}>
          {shown.map((e) => {
            const color = colorForEvent(e, categoryColors, calendarColors);
            return (
              <button key={e.id} type="button" onClick={() => onPick(e)}
                title={e.subject}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', border: 'none',
                  background: `${color}22`, borderLeft: `3px solid ${color}`,
                  borderRadius: 3, padding: '0 4px', marginBottom: 2, height: ALL_DAY_H - 2,
                  fontSize: '0.66rem', color: brand.deepBlue, fontWeight: 700, cursor: 'pointer',
                  ...CLIP,
                }}>
                {e.is_legacy_organizer && (
                  <i className="bi bi-lock-fill"
                    title={`Owned by ${e.organizer_email || 'a former firm account'} — can't be edited or cancelled here`}
                    style={{ color: brand.red, marginRight: 3 }} />
                )}
                {e.subject}
              </button>
            );
          })}
          {hidden > 0 && (
            <div style={{ fontSize: '0.62rem', color: brand.charcoal, paddingLeft: 4 }}>
              +{hidden} more
            </div>
          )}
        </div>
      )}

      <div style={{ position: 'relative', height: gridH }}>
        {Array.from({ length: Math.ceil(gridH / HOUR_H) + 1 }, (_, i) => (
          <div key={i} style={{ position: 'absolute', top: i * HOUR_H, left: 0, right: 0, borderTop: `1px solid ${brand.softGray}` }} />
        ))}
        {isToday && Number.isFinite(nowMinutes) && nowMinutes >= originMin && (
          <div style={{
            position: 'absolute', top: ((nowMinutes - originMin) / 60) * HOUR_H,
            left: 0, right: 0, height: 2, background: brand.tealBlue, zIndex: 3, pointerEvents: 'none',
          }} />
        )}
        {placed.map((e) => {
          const box = eventBox(e, originMin);
          const color = colorForEvent(e, categoryColors, calendarColors);
          const compact = box.height < 32;
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => onPick(e)}
              title={`${e.start_label}–${e.end_label} ${e.subject}${e.organizer ? `\nOrganizer: ${e.organizer}` : ''}`}
              style={{
                position: 'absolute', top: box.top, height: box.height,
                left: `${box.leftPct}%`, width: `calc(${box.widthPct}% - 2px)`,
                background: `${color}1f`, borderLeft: `3px solid ${color}`,
                border: 'none', borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: color,
                borderRadius: 4, padding: compact ? '0 3px' : '2px 4px',
                // `minWidth: 0` only — NOT the WRAP helper. Its
                // `overflowWrap: anywhere` is inherited, so it would break the
                // title mid-word however the child is styled.
                textAlign: 'left', overflow: 'hidden', cursor: 'pointer', zIndex: 2, minWidth: 0,
              }}
            >
              <div style={{
                color: brand.deepBlue, fontWeight: 700, fontSize: '0.68rem', lineHeight: 1.2,
                // Never `overflowWrap: anywhere` here — in a narrow week column it
                // splits words mid-syllable ("Traini / ng"). Clip at a word
                // boundary and let the tooltip carry the full text.
                ...(compact ? CLIP : { overflow: 'hidden', wordBreak: 'normal' }),
              }}>
                {/* Organized by an account from a pre-merger firm — nobody in
                    this tenant can edit or cancel it. A red padlock and nothing
                    else: the previous version was a chip reading "LOCKED", which
                    is wider than a stacked 1:1 column and got clipped away
                    entirely on exactly the meetings that needed it most.
                    The lock WINS over the repeat glyph when a meeting is both.
                    Space in a stacked column is scarce enough that two icons
                    crowd out the subject, and "you can't touch this" is the one
                    a person needs before they try — that it repeats is already
                    obvious from the grid, and the editor says so anyway. */}
                {e.is_legacy_organizer ? (
                  <i className="bi bi-lock-fill"
                    title={`Owned by ${e.organizer_email || 'a former firm account'} — can't be edited or cancelled here`}
                    style={{ color: brand.red, marginRight: 3 }} />
                ) : e.is_recurring && (
                  <i className="bi bi-arrow-repeat me-1" style={{ opacity: 0.7 }} />
                )}
                {e.subject}
              </div>
              {!compact && <div style={{ color: brand.charcoal, fontSize: '0.62rem' }}>{e.start_label}</div>}
            </button>
          );
        })}
      </div>

      {untimed.length > 0 && (
        <div style={{ fontSize: '0.62rem', color: brand.charcoal, padding: '2px 4px', ...WRAP }}>
          {untimed.length} with no readable time
        </div>
      )}
    </div>
  );
}
DayColumn.propTypes = {
  day: PropTypes.string.isRequired,
  events: PropTypes.array.isRequired,
  calendarColors: PropTypes.object.isRequired,
  categoryColors: PropTypes.object,
  originMin: PropTypes.number.isRequired,
  gridH: PropTypes.number.isRequired,
  nowMinutes: PropTypes.number,
  isToday: PropTypes.bool,
  allDayRows: PropTypes.number,
  onPick: PropTypes.func.isRequired,
};

// ── page ────────────────────────────────────────────────────────────────────

export default function Calendar() {
  const isMobile = useIsMobile();
  const [token, setToken] = useState(null);
  const [view, setView] = useState(isMobile ? 'day' : 'week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [selected, setSelected] = useState({});      // calendarId -> bool
  const [editing, setEditing] = useState(null);      // event being edited/created
  const [confirm, setConfirm] = useState(null);      // pending write awaiting confirmation
  const [writing, setWriting] = useState(false);     // a confirmed write is in flight
  const [banner, setBanner] = useState(null);

  const [loadCalendars, calRes] = useLazyGetCalendarsQuery();
  const [loadEvents, evRes] = useLazyGetCalendarEventsQuery();
  const [createEvent] = useCreateCalendarEventMutation();
  const [updateEvent] = useUpdateCalendarEventMutation();
  const [deleteEvent] = useDeleteCalendarEventMutation();
  const [inviteAttendees] = useInviteCalendarAttendeesMutation();

  const onToken = useCallback((accessToken) => {
    setToken(accessToken);
    loadCalendars({ graphToken: accessToken });
  }, [loadCalendars]);

  const { phase, authError, connecting, needsReload, connect, hubEmail } =
    useGraphToken(graphCalendarRequest, PATH, onToken);

  // Memoized because `|| []` would otherwise hand out a fresh array on every
  // render, churning every downstream memo and re-rendering the whole grid.
  const calendars = useMemo(() => calRes.data?.calendars || [], [calRes.data]);
  // Colour per calendar, mirroring Outlook where it can and falling back to the
  // validated palette where it can't. Computed over the FULL list so toggling a
  // calendar off never repaints the others.
  const calendarColors = useMemo(() => resolveCalendarColors(calendars), [calendars]);
  // Category name -> colour, from the mailbox's master list. Empty when
  // MailboxSettings.Read isn't consented; colorForEvent then name-matches the
  // six defaults before falling back to the calendar colour.
  const categoryColors = useMemo(
    () => buildCategoryColors(calRes.data?.categories), [calRes.data],
  );

  // Default: the user's own default calendar on, everything else off. Turning on
  // six advisors' calendars at once on first load would be unreadable.
  useEffect(() => {
    if (!calendars.length || Object.keys(selected).length) return;
    const init = {};
    calendars.forEach((c) => { init[c.id] = !!c.is_default; });
    if (!Object.values(init).some(Boolean) && calendars[0]) init[calendars[0].id] = true;
    setSelected(init);
  }, [calendars, selected]);

  const activeIds = useMemo(
    () => calendars.filter((c) => selected[c.id]).map((c) => c.id),
    [calendars, selected],
  );
  const days = view === 'week' ? 7 : 1;

  // Everything the fetch depends on is reduced to a PRIMITIVE before it reaches
  // a dependency array. `startOfWeek()` builds a new Date on every render, and an
  // array literal is a new reference on every render — either one makes the
  // useCallback below change identity each pass, which re-fires the effect, which
  // updates state, which re-renders. That is an infinite request loop, and it
  // looks like a working page because every request returns 200.
  const startIso = useMemo(
    () => iso(view === 'week' ? startOfWeek(anchor) : anchor),
    [view, anchor],
  );
  const calendarIdsKey = activeIds.join(',');

  // `force` maps to RTK Query's preferCacheValue. The effect below deliberately
  // does NOT force: with identical arguments it serves the cache and issues no
  // request at all, so even if a dependency slips and this re-fires every render,
  // the damage is bounded to React re-rendering rather than hammering Microsoft
  // Graph — which would get the whole tenant throttled. Writes force, because
  // after a save the cached copy is genuinely stale.
  // Hard idempotency guard, independent of React's dependency comparison.
  //
  // Belt and braces on purpose: the deps below are all primitives now, but this
  // effect reaches Microsoft Graph, and a re-render loop there gets the whole
  // TENANT throttled — not just this page. So the fetch refuses to repeat the
  // same window no matter how many times it is called. Only a real change of
  // window/calendars, or an explicit force after a write, gets through.
  const lastFetched = useRef(null);

  const fetchEvents = useCallback((force = false) => {
    if (!token || !calendarIdsKey) return;
    const key = `${calendarIdsKey}|${startIso}|${days}`;
    if (!force && lastFetched.current === key) return;
    lastFetched.current = key;
    loadEvents({ graphToken: token, calendarIds: calendarIdsKey, start: startIso, days }, !force);
  }, [token, calendarIdsKey, startIso, days, loadEvents]);

  useEffect(() => { fetchEvents(false); }, [fetchEvents]);

  const events = evRes.data?.events || [];
  const denied = evRes.data?.denied || [];
  const dayList = evRes.data?.days || [];
  // Meetings copied in during the tenant consolidation: the organizer is an
  // account that no longer exists here, so nobody in this tenant can edit or
  // cancel them. Counted server-side so the page and any cleanup tooling can't
  // disagree about what qualifies.
  const legacyCount = evRes.data?.legacy_count || 0;
  const legacyDomains = evRes.data?.legacy_domains || [];
  const timedForRange = useMemo(
    () => events.filter((e) => !e.is_all_day && Number.isFinite(e.start_minutes))
      .map((e) => ({ startMin: e.start_minutes, endMin: e.end_minutes ?? e.start_minutes + 30 })),
    [events],
  );
  const { fromHour, toHour } = hourRange(timedForRange);
  const originMin = fromHour * 60;
  const gridH = (toHour - fromHour) * HOUR_H;

  // Past four calendars, position carries identity instead of colour — there is
  // no fifth hue that stays distinguishable, so we facet rather than invent one.
  const faceted = activeIds.length > MAX_OVERLAY_CALENDARS;

  const eventsFor = (day, calId) => events.filter(
    (e) => e.day === day && (!calId || e.calendar_id === calId),
  );

  // Tallest all-day stack in the visible week, capped. Shared by every column so
  // the grids line up; 0 means no band at all, which is the common case.
  const allDayRows = useMemo(() => {
    const perDay = {};
    for (const e of events) {
      if (e.is_all_day) perDay[e.day] = (perDay[e.day] || 0) + 1;
    }
    const most = Math.max(0, ...Object.values(perDay));
    return Math.min(most, ALL_DAY_MAX_ROWS);
  }, [events]);

  // ── writes ────────────────────────────────────────────────────────────────

  /** A confirmed write, start to finish.
   *
   * The round trip to Graph is slow enough — a second or two, sometimes more —
   * that a confirmation button which stays live and unchanged reads as "that
   * didn't register", and people click it again. On create that makes two
   * meetings and emails every attendee twice. So the button reports that it is
   * working, and stays disabled until the answer comes back.
   */
  const runWrite = async (job) => {
    setBanner(null);
    setWriting(true);
    try {
      await job();
      setEditing(null);
      setConfirm(null);
      fetchEvents(true);
    } catch (e) {
      const status = e?.status;
      setConfirm(null);
      setBanner({
        variant: status === 409 ? 'warning' : 'danger',
        text: e?.data?.detail || 'That change could not be saved.',
      });
      if (status === 409) fetchEvents(true);
    } finally {
      setWriting(false);
    }
  };

  /** Nothing is written until the blast radius has been shown and accepted. */
  const askThenWrite = (action, form, job) => {
    const { total, external } = attendeeSplit(parseAttendees(form.attendeesText));
    setConfirm({
      action, total, external, subject: form.subject,
      series: writeTarget(form).series,
      run: () => runWrite(job),
    });
  };

  const saveEvent = (form) => {
    const attendees = parseAttendees(form.attendeesText);
    const target = writeTarget(form);
    const base = {
      graph_token: token,
      calendar_id: form.calendar_id,
      subject: form.subject,
      start_utc: fromInputValue(form.startInput),
      end_utc: fromInputValue(form.endInput),
      location: form.location || '',
      attendees,
    };
    if (!form.id) {
      askThenWrite('create', form, () => createEvent(base).unwrap());
      return;
    }

    // Adding people is the one change Graph can notify selectively, via forward.
    // Everything else — removals, time, subject, location — makes Exchange email
    // the whole attendee list, and there is no API to scope that.
    const diff = attendeeDiff(form.original?.attendees, attendees);
    if (onlyAddsAttendees({ ...form, attendees }, diff)) {
      setConfirm({
        action: 'invite',
        total: diff.added.length,
        external: attendeeSplit(diff.added).external,
        subject: form.subject,
        series: target.series,
        run: () => runWrite(() => inviteAttendees({
          eventId: target.eventId, graph_token: token,
          calendar_id: form.calendar_id, subject: form.subject, attendees: diff.added,
        }).unwrap()),
      });
      return;
    }
    askThenWrite('update', form, () =>
      updateEvent({
        eventId: target.eventId, etag: target.etag, series_scope: target.series, ...base,
      }).unwrap());
  };

  const removeEvent = (form) => {
    const { total } = attendeeSplit(parseAttendees(form.attendeesText));
    const target = writeTarget(form);
    askThenWrite('delete', form, () =>
      deleteEvent({
        eventId: target.eventId, graph_token: token, calendar_id: form.calendar_id,
        etag: target.etag, subject: form.subject, attendee_count: total,
        series_scope: target.series,
      }).unwrap());
  };

  /** Open an existing event for editing.
   *
   * Normalizes to the form shape once, here, and keeps a snapshot of the values
   * as loaded. That snapshot is what makes "only attendees were added" knowable
   * at save time — without it we could only ever assume the worst and notify
   * everyone.
   */
  const openEvent = (e) => {
    const base = blankFrom(e);
    setEditing({
      ...base,
      id: e.id, etag: e.etag, calendar_id: e.calendar_id, is_recurring: e.is_recurring,
      series_master_id: e.series_master_id,
      is_legacy_organizer: e.is_legacy_organizer, organizer_email: e.organizer_email,
      id_context: e.id_context, ical_uid: e.ical_uid,
      // Default to the narrow blast radius; widening it is an explicit choice.
      scope: 'occurrence',
      original: base,
    });
  };

  // ── render ────────────────────────────────────────────────────────────────

  let body;
  if (phase === 'connect') {
    body = (
      <div style={{ background: 'white', borderRadius: 12, padding: '2rem', textAlign: 'center' }}>
        <i className="bi bi-calendar-check" style={{ color: brand.tealBlue, fontSize: '2rem' }} />
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, marginTop: '0.75rem' }}>Connect Outlook</h5>
        <p style={{ color: brand.charcoal, fontSize: '0.9rem', maxWidth: 480, margin: '0 auto 1rem' }}>
          Calendar needs access to the calendars already shared with you, including permission to
          make changes. Connecting {hubEmail}.
        </p>
        {needsReload ? (
          <button type="button" onClick={() => window.location.reload()} style={btn(brand.deepBlue, 'white')}>
            <i className="bi bi-arrow-clockwise me-1" />Reload and try again
          </button>
        ) : (
          <button type="button" onClick={() => connect(isMobile)} disabled={connecting}
            style={btn(connecting ? brand.softGray : brand.goldenrod, brand.deepBlue)}>
            {connecting ? 'Waiting for Microsoft…' : 'Connect Outlook'}
          </button>
        )}
        {authError && <Alert variant="warning" className="py-2 mt-3 mb-0" style={{ fontSize: '0.82rem' }}>{authError}</Alert>}
      </div>
    );
  } else if (calRes.isError) {
    body = (
      <Alert variant="danger" className="py-2">
        {calRes.error?.data?.detail || 'Could not load your calendars.'}
      </Alert>
    );
  } else if (calRes.isFetching || !calendars.length) {
    body = <div className="text-center p-5"><Spinner style={{ color: brand.tealBlue }} /></div>;
  } else {
    body = (
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
        {/* Calendar rail — also the colour legend */}
        <div style={{
          flex: isMobile ? '1 1 auto' : '0 0 220px', width: isMobile ? '100%' : 220,
          background: 'white', borderRadius: 12, padding: '0.75rem',
        }}>
          <div style={{ fontWeight: 800, color: brand.deepBlue, fontSize: '0.8rem', marginBottom: '0.5rem' }}>
            Calendars
          </div>
          {calendars.map((c) => (
            <label key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0',
              cursor: 'pointer', minHeight: isMobile ? TAP : undefined, ...WRAP,
            }}>
              <input
                type="checkbox"
                checked={!!selected[c.id]}
                onChange={() => setSelected((p) => ({ ...p, [c.id]: !p[c.id] }))}
              />
              <span style={{
                width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                background: calendarColors[c.id] || '#58585b',
              }} />
              <span style={{ minWidth: 0, flex: 1, fontSize: '0.78rem', color: brand.deepBlue }}>
                {c.name}
                {c.owner_name && !c.is_default && (
                  <span style={{ display: 'block', color: brand.charcoal, fontSize: '0.68rem' }}>{c.owner_name}</span>
                )}
              </span>
              {!c.can_edit && (
                <span title="You can view this calendar but not change it"
                  style={{ color: brand.charcoal, fontSize: '0.62rem', fontWeight: 700 }}>VIEW</span>
              )}
            </label>
          ))}
          {faceted && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.68rem', color: brand.charcoal, ...WRAP }}>
              More than {MAX_OVERLAY_CALENDARS} selected — showing them side by side, since colour alone
              can&apos;t tell that many apart reliably.
            </div>
          )}
        </div>

        {/* Grid */}
        <div style={{ flex: 1, minWidth: 0, background: 'white', borderRadius: 12, padding: '0.75rem', overflowX: 'auto' }}>
          {denied.length > 0 && (
            <Alert variant="warning" className="py-2" style={{ fontSize: '0.8rem' }}>
              {denied.length} calendar{denied.length === 1 ? '' : 's'} could not be loaded
              {denied.some((d) => d.reason === 'permission') && ' — you may no longer have access'}.
              The rest are shown.
            </Alert>
          )}
          {legacyCount > 0 && (
            <Alert variant="secondary" className="py-2" style={{ fontSize: '0.82rem' }}>
              <i className="bi bi-clock-history me-2" />
              <strong>{legacyCount}</strong> {legacyCount === 1 ? 'meeting' : 'meetings'} in view
              {legacyDomains.length ? ` came across from ${legacyDomains.join(', ')}` : ' came from a former firm'} and
              still {legacyCount === 1 ? 'belongs' : 'belong'} to the organizer&apos;s old account, so
              {legacyCount === 1 ? ' it can' : ' they can'}&apos;t be edited or cancelled here. Marked below with
              <i className="bi bi-clock-history mx-1" />.
            </Alert>
          )}
          {evRes.isFetching ? (
            <div className="text-center p-4"><Spinner size="sm" style={{ color: brand.tealBlue }} /></div>
          ) : !activeIds.length ? (
            <div style={{ color: brand.charcoal, fontSize: '0.85rem', padding: '1rem' }}>
              Select a calendar to see it here.
            </div>
          ) : (
            <div style={{ display: 'flex', minWidth: faceted ? activeIds.length * 220 : undefined }}>
              {/* Hour gutter. The two hidden spacers mirror each day column's
                  header and all-day band so the labels stay level with the grid
                  whatever the week contains. */}
              <div style={{ flex: `0 0 ${GUTTER_W}px` }}>
                <div style={{ ...colHeadStyle(false), visibility: 'hidden' }} aria-hidden="true">&nbsp;</div>
                {allDayRows > 0 && (
                  <div style={{ ...allDayBandStyle(allDayRows), visibility: 'hidden' }} aria-hidden="true" />
                )}
                {Array.from({ length: toHour - fromHour + 1 }, (_, i) => (
                  <div key={i} style={{
                    height: HOUR_H, fontSize: '0.66rem', color: brand.charcoal,
                    textAlign: 'right', paddingRight: 6, transform: 'translateY(-6px)',
                  }}>
                    {hourLabel(fromHour + i)}
                  </div>
                ))}
              </div>

              {faceted
                ? activeIds.map((calId) => (
                  <div key={calId} style={{ flex: 1, minWidth: 200 }}>
                    <div style={{
                      textAlign: 'center', fontSize: '0.7rem', fontWeight: 800,
                      color: calendarColors[calId] || '#58585b', paddingBottom: 2,
                    }}>
                      {calendars.find((c) => c.id === calId)?.name}
                    </div>
                    <div style={{ display: 'flex' }}>
                      {dayList.map((d) => (
                        <DayColumn key={d} day={d} events={eventsFor(d, calId)} calendarColors={calendarColors} categoryColors={categoryColors}
                          originMin={originMin} gridH={gridH} nowMinutes={evRes.data?.now_minutes}
                          isToday={d === evRes.data?.today} allDayRows={allDayRows} onPick={openEvent} />
                      ))}
                    </div>
                  </div>
                ))
                : dayList.map((d) => (
                  <DayColumn key={d} day={d} events={eventsFor(d)} calendarColors={calendarColors} categoryColors={categoryColors}
                    originMin={originMin} gridH={gridH} nowMinutes={evRes.data?.now_minutes}
                    isToday={d === evRes.data?.today} allDayRows={allDayRows} onPick={openEvent} />
                ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <div style={{
        background: brand.deepBlue, borderRadius: 12, padding: isMobile ? '1.25rem' : '1.5rem 2rem',
        marginBottom: '1rem', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3, background: brand.goldenrod }} />
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, margin: '0 0 2px' }}>
              Outlook
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0, fontSize: isMobile ? '1.25rem' : undefined }}>Calendar</h3>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setAnchor(addDays(anchor, view === 'week' ? -7 : -1))} style={navBtn}>
              <i className="bi bi-chevron-left" />
            </button>
            <button type="button" onClick={() => setAnchor(new Date())} style={navBtn}>Today</button>
            <button type="button" onClick={() => setAnchor(addDays(anchor, view === 'week' ? 7 : 1))} style={navBtn}>
              <i className="bi bi-chevron-right" />
            </button>
            {!isMobile && (
              <button type="button" onClick={() => setView(view === 'week' ? 'day' : 'week')} style={navBtn}>
                {view === 'week' ? 'Day view' : 'Week view'}
              </button>
            )}
            <button type="button" disabled={phase !== 'ready'}
              onClick={() => setEditing(blankEvent(activeIds[0] || ''))}
              style={{ ...navBtn, background: brand.goldenrod, color: brand.deepBlue, borderColor: brand.goldenrod }}>
              <i className="bi bi-plus-lg me-1" />New
            </button>
          </div>
        </div>
      </div>

      {banner && (
        <Alert variant={banner.variant} className="py-2" onClose={() => setBanner(null)} dismissible
          style={{ fontSize: '0.85rem' }}>
          {banner.text}
        </Alert>
      )}

      {body}

      {editing && (
        <EventModal
          form={editing}
          calendars={calendars}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={saveEvent}
          onDelete={removeEvent}
        />
      )}

      {confirm && (
        <ConfirmModal
          info={confirm}
          busy={writing}
          onCancel={() => { if (!writing) setConfirm(null); }}
        />
      )}
    </div>
  );
}

// ── helpers + small components ──────────────────────────────────────────────

const navBtn = {
  border: '1px solid rgba(255,255,255,0.35)', background: 'transparent', color: 'white',
  borderRadius: 8, padding: '0.35rem 0.7rem', fontSize: '0.8rem', fontWeight: 700,
  cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 34,
};
const btn = (bg, fg) => ({
  minHeight: TAP, padding: '0.6rem 1.4rem', borderRadius: 8, border: 'none',
  background: bg, color: fg, fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer',
});

/** Which event a write should actually land on, and with what etag.
 *
 * A repeating meeting is two things in Graph: the occurrence the user clicked,
 * and the series master behind it. Writing to the occurrence changes that day
 * only; writing to the master changes every occurrence. The grid only ever holds
 * the OCCURRENCE's etag, and the master carries a different one — so on the
 * series path we send no etag and let the server look the current one up, rather
 * than send an etag that is guaranteed to fail the If-Match check.
 */
function writeTarget(form) {
  const series = form.scope === 'series' && !!form.series_master_id;
  return {
    series,
    eventId: series ? form.series_master_id : form.id,
    etag: series ? null : form.etag,
  };
}

function blankEvent(calendarId) {
  const start = new Date(); start.setMinutes(0, 0, 0); start.setHours(start.getHours() + 1);
  const end = new Date(start); end.setHours(end.getHours() + 1);
  return {
    id: null, etag: null, calendar_id: calendarId, subject: '',
    startInput: toInputValue(start.toISOString()), endInput: toInputValue(end.toISOString()),
    location: '', attendees: [], attendeesText: '',
  };
}
function blankFrom(ev) {
  return {
    subject: ev.subject, location: ev.location || '',
    startInput: toInputValue(ev.start), endInput: toInputValue(ev.end),
    attendees: (ev.attendees || []).map((a) => ({ email: a.email, name: a.name })),
    // The editable text, held raw so a half-typed address survives a keystroke.
    // `attendees` above stays as the as-loaded snapshot for the additions-only diff.
    attendeesText: (ev.attendees || []).map((a) => a.email).filter(Boolean).join(', '),
  };
}

function EventModal({ form, calendars, onChange, onClose, onSave, onDelete }) {
  // The form arrives either as a picked event (Graph shape) or a blank draft
  // (input shape). Normalize once so the fields below have one contract.
  const f = form.startInput !== undefined ? form : { ...form, ...blankFrom(form) };
  const set = (k, v) => onChange({ ...f, [k]: v });
  const cal = calendars.find((c) => c.id === f.calendar_id);
  const readOnly = f.id && cal && !cal.can_edit;
  const { total, external } = attendeeSplit(parseAttendees(f.attendeesText));
  // Built once so the same string is both displayed and copied — a command that
  // differs from the one on screen is the worst kind of copy button.
  const transferCmd = `Invoke-ChangeMeetingOrganizer -Identity ${cal?.owner_email || '<mailbox>'}`
    + ` -EventId ${f.series_master_id || f.id} -NewOrganizer <waypoint mailbox>`;

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton style={{ borderBottom: `1px solid ${brand.softGray}` }}>
        <Modal.Title style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1.05rem' }}>
          {f.id ? 'Edit meeting' : 'New meeting'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {f.is_recurring && (
          <Alert variant="warning" className="py-2" style={{ fontSize: '0.82rem' }}>
            <div className="mb-2">This is one occurrence of a <strong>repeating</strong> meeting.</div>
            <Form.Check
              type="radio" id="scope-occurrence" name="recurrence-scope"
              checked={f.scope !== 'series'}
              onChange={() => set('scope', 'occurrence')}
              label="Just this occurrence"
            />
            <Form.Check
              type="radio" id="scope-series" name="recurrence-scope"
              checked={f.scope === 'series'}
              onChange={() => set('scope', 'series')}
              disabled={!f.series_master_id}
              label="The entire series — every occurrence, past and future"
            />
            {f.scope === 'series' && (
              <div className="mt-2" style={{ fontSize: '0.78rem' }}>
                Changing the time on the series moves <em>every</em> occurrence, the same as
                editing the series in Outlook.
              </div>
            )}
          </Alert>
        )}
        {f.is_legacy_organizer && (
          <Alert variant="warning" className="py-2" style={{ fontSize: '0.82rem' }}>
            <div style={{ fontWeight: 700 }}>
              <i className="bi bi-clock-history me-1" />
              This meeting still belongs to a former firm account
            </div>
            <div className="mt-1">
              It was organized by <strong>{f.organizer_email}</strong>, which moved to Prosperity during the
              tenant merge. Microsoft treats everyone here as an attendee, so saving or cancelling
              will fail — that isn&apos;t a permissions problem and more access won&apos;t fix it.
              Ownership can be transferred with Exchange Online PowerShell — attendees inside
              Prosperity are updated silently, without re-inviting anyone:
            </div>
            {f.id_context === 'owner' && (
              <>
                <div style={{
                  marginTop: '0.5rem', background: 'white', border: `1px solid ${brand.softGray}`,
                  borderRadius: 6, padding: '0.4rem 0.55rem',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.68rem', ...WRAP, userSelect: 'all',
                }}>
                  {transferCmd}
                </div>
                <div style={{ marginTop: '0.35rem' }}>
                  <CopyButton value={transferCmd} label="Copy command" />
                </div>
              </>
            )}
            <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginTop: '0.3rem' }}>
              Exchange refuses a transfer to the same mailbox, so this goes via an intermediate
              account and then back. Only future instances move, and a series can only be
              transferred once — check the meeting before running it.
            </div>
          </Alert>
        )}
        {f.id && (
          <Field label="Event ID">
            {/* Graph event ids are scoped to the mailbox they were read from. When
                we can read the calendar through its OWNER's mailbox the id works
                with Exchange cmdlets; when we can only read it through the signed-in
                user's copy it does NOT, and pasting it gets "The store ID provided
                isn't an ID of an item". So say which one this is rather than hand
                over something that looks right and isn't. For a repeating meeting
                the cmdlet needs the SERIES id, never a single occurrence. */}
            <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'stretch' }}>
              <input readOnly value={f.series_master_id || f.id}
                onFocus={(e) => e.target.select()}
                style={{
                  ...input, flex: 1, minWidth: 0,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.72rem',
                  borderColor: f.id_context === 'owner' ? brand.softGray : brand.red,
                }} />
              <CopyButton value={f.series_master_id || f.id} label="Copy ID" />
            </div>
            <div style={{ color: brand.charcoal, fontSize: '0.7rem', marginTop: '0.25rem' }}>
              {f.series_master_id ? 'Series id. ' : 'Single meeting. '}
              {f.id_context === 'owner' ? (
                <>Read from the owner&apos;s mailbox — valid for Exchange PowerShell. Click to select, then copy.</>
              ) : (
                <span style={{ color: brand.red }}>
                  Read through your own mailbox, so this id will <strong>not</strong> work with
                  Invoke-ChangeMeetingOrganizer. Needs Calendars.ReadWrite.Shared consent, or the
                  calendar is a secondary one.
                </span>
              )}
            </div>
          </Field>
        )}
        {readOnly && (
          <Alert variant="secondary" className="py-2" style={{ fontSize: '0.82rem' }}>
            You have view-only access to this calendar, so this meeting can&apos;t be changed here.
          </Alert>
        )}

        <Field label="Calendar">
          <select value={f.calendar_id || ''} onChange={(e) => set('calendar_id', e.target.value)}
            disabled={!!f.id} style={input}>
            <option value="">Choose…</option>
            {calendars.filter((c) => c.can_edit || c.id === f.calendar_id).map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.owner_name ? ` — ${c.owner_name}` : ''}</option>
            ))}
          </select>
        </Field>
        <Field label="Subject">
          <input value={f.subject || ''} onChange={(e) => set('subject', e.target.value)} style={input} />
        </Field>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Field label="Start"><input type="datetime-local" value={f.startInput || ''} onChange={(e) => set('startInput', e.target.value)} style={input} /></Field>
          <Field label="End"><input type="datetime-local" value={f.endInput || ''} onChange={(e) => set('endInput', e.target.value)} style={input} /></Field>
        </div>
        <Field label="Location">
          <input value={f.location || ''} onChange={(e) => set('location', e.target.value)} style={input} />
        </Field>
        <Field label={`Attendees (${total}${external ? `, ${external} outside the firm` : ''})`}>
          <input
            value={f.attendeesText ?? ''}
            onChange={(e) => set('attendeesText', e.target.value)}
            placeholder="name@example.com, other@example.com"
            style={input}
          />
        </Field>
        <div style={{ color: brand.charcoal, fontSize: '0.72rem', marginTop: '0.4rem' }}>
          The meeting body isn&apos;t shown or changed here, so any Zoom link it carries stays as it is.
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: `1px solid ${brand.softGray}`, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {f.id && !readOnly && (
            <button type="button" onClick={() => onDelete(f)} style={{ ...smallBtn, borderColor: brand.red, color: brand.red }}>
              Delete
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button type="button" onClick={onClose} style={smallBtn}>Close</button>
          <button
            type="button"
            onClick={() => onSave(f)}
            disabled={readOnly || !f.subject || !f.calendar_id || !f.startInput || !f.endInput}
            style={{ ...smallBtn, background: brand.deepBlue, color: 'white', borderColor: brand.deepBlue }}
          >
            Save
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
EventModal.propTypes = {
  form: PropTypes.object.isRequired,
  calendars: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

/** Nothing is written until this has been read and accepted. Naming the number
 *  of outsiders is the part that makes someone pause before moving a client. */
/** Copy-to-clipboard for values nobody should be retyping.
 *
 * An Exchange event id is ~150 characters of base64 that wraps across lines and
 * fails silently if a single character is lost — selecting it by hand is exactly
 * the wrong way to move it. Falls back to a hidden textarea + execCommand for
 * browsers without the async clipboard API, since that API is absent entirely
 * outside a secure context and the button would otherwise do nothing at all.
 */
function CopyButton({ value, label = 'Copy' }) {
  const [done, setDone] = useState(false);

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setDone(true);
      window.setTimeout(() => setDone(false), 1500);
    } catch {
      // Leave the button as it was — the value stays selectable by hand, and a
      // false "Copied" would be worse than no feedback.
    }
  };

  return (
    <button type="button" onClick={copy} title={`${label} to clipboard`}
      style={{
        ...smallBtn, whiteSpace: 'nowrap', flex: '0 0 auto',
        borderColor: done ? brand.green : brand.softGray,
        color: done ? brand.green : brand.charcoal,
      }}>
      <i className={`bi ${done ? 'bi-check-lg' : 'bi-clipboard'} me-1`} />
      {done ? 'Copied' : label}
    </button>
  );
}
CopyButton.propTypes = { value: PropTypes.string.isRequired, label: PropTypes.string };

function ConfirmModal({ info, busy, onCancel }) {
  const verb = info.action === 'delete' ? 'cancel' : info.action === 'create' ? 'send' : 'update';
  // Belt and braces against the double-submit. `busy` disables the button, but
  // only after a state round trip — two clicks inside the same frame would both
  // land. This ref closes that window synchronously, on the first click.
  const fired = useRef(false);
  const go = () => {
    if (fired.current || busy) return;
    fired.current = true;
    info.run();
  };
  const dismiss = () => { if (!busy) onCancel(); };
  // While a write is in flight the modal can't be dismissed: the meeting is
  // already changing, and closing would leave the user with no idea whether it
  // took. `backdrop="static"` covers the click-outside and Esc routes that
  // bypass the Cancel button entirely.
  // "Every occurrence" is the detail someone needs before they press the button,
  // not after — a series change can move a year of meetings for a room of people.
  const seriesNote = info.series && (
    <Alert variant="warning" className="py-2 mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
      <i className="bi bi-arrow-repeat me-1" />
      This applies to the <strong>entire series</strong>, not just the day you opened.
      Every occurrence will be {info.action === 'delete' ? 'cancelled' : 'changed'}.
    </Alert>
  );
  // The invite path is the one case where the blast radius is deliberately small,
  // so it gets its own wording rather than the generic "N attendees" line.
  if (info.action === 'invite') {
    return (
      <Modal show onHide={dismiss} backdrop={busy ? 'static' : true} keyboard={!busy} centered size="sm">
        <Modal.Body>
          <div style={{ fontWeight: 800, color: brand.deepBlue, marginBottom: '0.5rem' }}>
            <i className="bi bi-person-plus-fill me-2" style={{ color: brand.green }} />
            Only the people you added will be emailed
          </div>
          <div style={{ color: brand.charcoal, fontSize: '0.88rem', ...WRAP }}>
            {info.total} {info.total === 1 ? 'person' : 'people'} will get an invitation
            {info.external > 0 && <strong>, {info.external} outside the firm</strong>}.
            Everyone already on the meeting is left alone.
          </div>
          {info.subject && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: brand.deepBlue, fontWeight: 700, ...WRAP }}>
              {info.subject}
            </div>
          )}
          {seriesNote}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" onClick={dismiss} disabled={busy} style={dimWhen(busy, smallBtn)}>
            Cancel
          </button>
          <button type="button" onClick={go} disabled={busy}
            style={dimWhen(busy, { ...smallBtn, background: brand.green, color: 'white', borderColor: 'transparent' })}>
            {busy ? <Working label="Sending…" /> : 'Send invitation'}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
  return (
    <Modal show onHide={dismiss} backdrop={busy ? 'static' : true} keyboard={!busy} centered size="sm">
      <Modal.Body>
        <div style={{ fontWeight: 800, color: brand.deepBlue, marginBottom: '0.5rem' }}>
          <i className="bi bi-envelope-exclamation me-2" style={{ color: brand.goldenrod }} />
          Outlook will email people
        </div>
        <div style={{ color: brand.charcoal, fontSize: '0.88rem', ...WRAP }}>
          {info.total === 0
            ? `This meeting has no attendees, so nobody will be emailed.`
            : `This will ${verb === 'cancel' ? 'send a cancellation to' : 'email'} ${info.total} attendee${info.total === 1 ? '' : 's'}`}
          {info.external > 0 && (
            <strong style={{ color: brand.red }}>, including {info.external} outside the firm</strong>
          )}
          {info.total > 0 && '.'}
        </div>
        {info.subject && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: brand.deepBlue, fontWeight: 700, ...WRAP }}>
            {info.subject}
          </div>
        )}
        {seriesNote}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" onClick={dismiss} disabled={busy} style={dimWhen(busy, smallBtn)}>
          Cancel
        </button>
        <button type="button" onClick={go} disabled={busy}
          style={dimWhen(busy, { ...smallBtn, background: info.action === 'delete' ? brand.red : brand.deepBlue, color: 'white', borderColor: 'transparent' })}>
          {busy
            ? <Working label={info.action === 'delete' ? 'Cancelling…' : 'Saving…'} />
            : info.action === 'delete'
              ? (info.series ? 'Delete every occurrence' : 'Delete meeting')
              : 'Confirm'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
ConfirmModal.propTypes = {
  info: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
};

/** Spinner + label for a button that is mid-write. The label changes too, not
 *  just the spinner — "Saving…" is what tells someone it registered. */
function Working({ label }) {
  return (
    <>
      <Spinner animation="border" size="sm" className="me-2" style={{ borderWidth: 2 }} />
      {label}
    </>
  );
}
Working.propTypes = { label: PropTypes.string.isRequired };

/** Disabled buttons here are styled inline, so they need the dimming applied by
 *  hand — without it a disabled button looks identical to a live one. */
function dimWhen(busy, style) {
  return busy ? { ...style, opacity: 0.65, cursor: 'not-allowed' } : style;
}

function Field({ label, children }) {
  return (
    <div style={{ flex: 1, marginBottom: '0.6rem' }}>
      <label style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, fontWeight: 700, marginBottom: 3 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const input = {
  width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, background: 'white',
};
const smallBtn = {
  border: `1px solid ${brand.softGray}`, background: 'white', color: brand.deepBlue,
  borderRadius: 8, padding: '0.4rem 0.8rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
};
